import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { logger } from '@/lib/logger';
import { tr } from '@/lib/i18n';

export interface ChildProfile {
  id: string;
  name: string;
  brain_profile: 'INTENSE' | 'DISTRACTED' | 'DEFIANT';
  development_phase?: 'baby' | 'toddler' | 'child' | 'preteen' | null;
  created_at?: string | null;
  photo_url?: string | null;
  age?: number | null;
  shared_from_owner_id?: string | null;
  shared_from_owner_name?: string | null;
}

interface ChildProfilesContextValue {
  childProfiles: ChildProfile[];
  activeChild: ChildProfile | null;
  loading: boolean;
  onboardingRequired: boolean;
  refreshChildren: () => Promise<void>;
  setActiveChild: (childId: string | ChildProfile) => void;
}

// Exportado para a bancada de prova (`src/preview/main.tsx`) montar a tela com
// um valor falso, sem arrastar AuthProvider e Supabase atrás. O app segue
// consumindo pelo `useChildProfiles` abaixo.
export const ChildProfilesContext = createContext<ChildProfilesContextValue | undefined>(undefined);

function getStorageKey(userId: string) {
  return `active_child_${userId}`;
}

export function ChildProfilesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [childProfiles, setChildProfiles] = useState<ChildProfile[]>([]);
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadStoredActiveChild = (userId: string) => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(getStorageKey(userId));
    } catch (error) {
      logger.warn('Unable to read active child from storage', error);
      return null;
    }
  };

  const persistActiveChild = (userId: string, childId: string | null) => {
    if (typeof window === 'undefined') return;
    try {
      if (childId) {
        window.localStorage.setItem(getStorageKey(userId), childId);
      } else {
        window.localStorage.removeItem(getStorageKey(userId));
      }
    } catch (error) {
      logger.warn('Unable to persist active child selection', error);
    }
  };

  const refreshChildren = useCallback(async () => {
    if (!user?.profileId) {
      setChildProfiles([]);
      setActiveChildId(null);
      return;
    }

    setLoading(true);
    try {
      // Friend-type shares do not grant access to the owner's children
      const { data: shares } = await supabase
        .from('family_shares')
        .select('owner_id')
        .eq('partner_id', user.profileId)
        .eq('status', 'accepted')
        .eq('relationship_type', 'family');

      const sharedOwnerIds = Array.from(new Set((shares || []).map(s => s.owner_id).filter(Boolean))) as string[];
      const parentIds = [user.profileId, ...sharedOwnerIds];

      const [childrenRes, ownersRes] = await Promise.all([
        supabase
          .from('child_profiles')
          .select('id, name, brain_profile, development_phase, created_at, photo_url, age, parent_id')
          .in('parent_id', parentIds)
          .order('created_at', { ascending: true }),
        sharedOwnerIds.length > 0
          ? supabase.from('profiles').select('id, name, user_display_name').in('id', sharedOwnerIds)
          : Promise.resolve({ data: [] as Array<{ id: string; name: string | null; user_display_name: string | null }> }),
      ]);

      const { data, error } = childrenRes;
      if (error) {
        logger.error('Failed to load child profiles', error);
        setChildProfiles([]);
        return;
      }

      const ownersById = (ownersRes.data || []).reduce((acc, o) => {
        acc[o.id] = (o.user_display_name || o.name || tr('Family')).split(' ')[0];
        return acc;
      }, {} as Record<string, string>);

      const formatted = (data || []).map((child) => {
        const isShared = child.parent_id !== user.profileId;
        return {
          id: child.id,
          name: child.name,
          brain_profile: child.brain_profile as ChildProfile['brain_profile'],
          development_phase: child.development_phase as ChildProfile['development_phase'],
          created_at: child.created_at,
          photo_url: child.photo_url,
          age: child.age,
          shared_from_owner_id: isShared ? child.parent_id : null,
          shared_from_owner_name: isShared ? (ownersById[child.parent_id] || null) : null,
        };
      });

      setChildProfiles(formatted);

      // Only auto-select if we have profiles and user ID
      if (user?.id && formatted.length > 0) {
        const storedId = loadStoredActiveChild(user.id);
        if (storedId && formatted.some((child) => child.id === storedId)) {
          setActiveChildId(storedId);
        } else {
          const fallbackId = formatted[0].id;
          setActiveChildId(fallbackId);
          persistActiveChild(user.id, fallbackId);
        }
      } else {
        setActiveChildId(null);
        if (user?.id) {
          persistActiveChild(user.id, null);
        }
      }
    } catch (error) {
      logger.error('Unexpected error loading child profiles', error);
      setChildProfiles([]);
      setActiveChildId(null);
    } finally {
      setLoading(false);
    }
  }, [user?.profileId, user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setChildProfiles([]);
      setActiveChildId(null);
      return;
    }

    const stored = loadStoredActiveChild(user.id);
    if (stored) {
      setActiveChildId(stored);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profileId]);

  const setActiveChild = useCallback((childId: string | ChildProfile) => {
    if (!user?.id) return;

    // Extract ID if a full profile object is passed
    const id = typeof childId === 'string' ? childId : childId.id;

    setActiveChildId(id);
    persistActiveChild(user.id, id);
  }, [user?.id]);

  const activeChild = useMemo(() => {
    if (!activeChildId) return null;
    return childProfiles.find((child) => child.id === activeChildId) || null;
  }, [activeChildId, childProfiles]);

  const onboardingRequired = !!user && !loading && childProfiles.length === 0;

  const contextValue = useMemo(() => ({
    childProfiles,
    activeChild,
    loading,
    onboardingRequired,
    refreshChildren,
    setActiveChild,
  }), [childProfiles, activeChild, loading, onboardingRequired, refreshChildren, setActiveChild]);

  return (
    <ChildProfilesContext.Provider value={contextValue}>
      {children}
    </ChildProfilesContext.Provider>
  );
}

export function useChildProfiles() {
  const context = useContext(ChildProfilesContext);
  if (!context) {
    throw new Error('useChildProfiles must be used within a ChildProfilesProvider');
  }
  return context;
}
